import { useState, useContext, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import moment from 'moment-timezone'

import { List, ListItemText, ListItemButton, ListItemIcon, Typography, Button, FormControlLabel, Switch, FormLabel, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Select, MenuItem, RadioGroup, Radio, Divider } from '@mui/material'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'

import { AppContext } from '/providers/app-context'

import { getCreditsPriceList, getClientPaymentCard, getVatRate } from '/providers/studio-api'
import { getlastBundlePurchase, purchaseCreditBundle } from '/providers/clients-api'


const CreditsPriceList = ({ refreshBalance, balance }) => {

  const { apiSource, apiKey } = useContext(AppContext)
  const [cardValid, setCardValid] = useState(false)
  const [priceList, setPriceList] = useState([])
  const [selectedBundle, setSelectedBundle] = useState(null)
  const [processing, setProcessing] = useState(false)
  const [showIncVat, setShowIncVat] = useState(true)
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false)
  const [purchaseResult, setPurchaseResult] = useState(null)
  const [topupBundleName, setTopupBundleName] = useState("")
  const [threshold, setThreshold] = useState("10")

  const lastPurchasedBundle = useRef(null)
  const vatRate = useRef(0)

  useEffect(() => {
    getVatData()
    getSavedCard()
  }, [])

  const getVatData = async () => {
    const v = await getVatRate({ apiSource, apiKey })
    vatRate.current = v.vat_rate
    await getLastPurchase()
  }

  const getLastPurchase = async () => {
    const p = await getlastBundlePurchase({ apiSource, apiKey })
    lastPurchasedBundle.current = p.bundle
    await getPriceList()
  }
  
  const getSavedCard = async () => {
    const c = await getClientPaymentCard({ apiSource, apiKey })

    if (c.hasSavedCard) {

      const now = moment()
      const daysLeft = moment(c.card.expiry_date, "YYYY-MM-DD").diff(now, "days")
      const valid = daysLeft > 0

      setCardValid(valid)
    }
  }

  const getPriceList = async () => {
    const p = await getCreditsPriceList({ apiSource, apiKey })
    setPriceList(p.priceList)

    let sb = p.priceList[0]

    if (lastPurchasedBundle.current) {
      sb = p.priceList.filter(p => p.bundle_name === lastPurchasedBundle.current.bundle_name)[0]
    }
    setSelectedBundle(sb)
  }

  const purchaseBundle = async() => {
    const purchase_bundle_name = selectedBundle.bundle_name
    setPurchaseResult(null)
    setProcessing(true)
    const result = await purchaseCreditBundle({
      apiSource,
      apiKey,
      purchase_bundle_name,
      auto_topup_bundle: topupBundleName || null,
      threshold: parseInt(threshold, 10)
    })
    setProcessing(false)
    setPurchaseResult(result.ok ? "success" : "failure")
    if (result.ok) refreshBalance()
  }

  const dismissPurchaseDialog = () => {
    if (processing) return
    setPurchaseDialogOpen(false)
    setPurchaseResult(null)
  }

  const openPurchaseDialog = () => {
    setTopupBundleName(balance?.auto_topup ? balance.topup_bundle || "" : "")
    setThreshold(String(balance?.threshold || 10))
    setPurchaseResult(null)
    setPurchaseDialogOpen(true)
  }

  const getDisplayPrice = price => {
    const basePrice = Number(price) || 0
    const multiplier = showIncVat ? 1 + (Number(vatRate.current) || 0) : 1
    return (basePrice * multiplier).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatPrice = price => {
    return (Number(price) || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const selectedTopupBundle = priceList.find(bundle => bundle.bundle_name === topupBundleName)

  return (
    <>
      <Dialog
        open={purchaseDialogOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return
          dismissPurchaseDialog()
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Credit purchase</DialogTitle>
        <DialogContent>
          <div className='flex flex-col gap-5 min-w-[420px] py-5'>
            {!processing && purchaseResult === null ? (
              <>
                <Typography>Please confirm the bundle you want to purchase now.</Typography>
                <Divider />
                <PurchaseItem title="Bundle" value={selectedBundle?.bundle_name || "-"} />
                <PurchaseItem title="Credits" value={selectedBundle ? `${parseInt(selectedBundle.credits,10).toLocaleString()} credits` : "-"} />
                <PurchaseItem title="Overdraft" value={selectedBundle ? `${parseInt(selectedBundle.overdraft,10).toLocaleString()} credits` : "-"} />
                <PurchaseItem title="Price" value={selectedBundle ? `£${getDisplayPrice(selectedBundle.price)}` : "-"} />

                <div className='flex flex-col gap-2'>
                  <FormLabel>Auto top-up bundle</FormLabel>
                  <Select size='small' value={topupBundleName} onChange={e => setTopupBundleName(e.target.value)}>
                    <MenuItem value="">No Auto-top-up</MenuItem>
                    {priceList.map(bundle => (
                    <MenuItem key={bundle.bundle_name} value={bundle.bundle_name}>
                      <span>
                        {bundle.bundle_name} ({parseInt(bundle.credits,10).toLocaleString()} credits, <span className='line-through'>£{formatPrice(bundle.price)}</span> £{formatPrice(bundle.auto_price)})
                      </span>
                    </MenuItem>
                    ))}
                  </Select>
                  <Typography variant='body2'>
                    {topupBundleName
                      ? 'Automatic top-ups use the discounted auto top-up price shown below. The discounted price applies to future automatic top-ups, not to this initial purchase.'
                      : 'If auto top-up is not enabled, we will email you when your balance reaches the selected threshold.'}
                  </Typography>
                  <Typography variant='body2'>
                    We will email you whenever an automatic top-up is processed.
                  </Typography>
                  {selectedTopupBundle ? (
                    <Typography variant='body2'>
                      Auto top-up bundle: {selectedTopupBundle.bundle_name}, {parseInt(selectedTopupBundle.credits,10).toLocaleString()} credits, overdraft {parseInt(selectedTopupBundle.overdraft,10).toLocaleString()} credits, standard price £{getDisplayPrice(selectedTopupBundle.price)}, discounted auto top-up price £{formatPrice(selectedTopupBundle.auto_price)}
                    </Typography>
                  ) : null}
                </div>

                <div className='flex flex-col gap-2'>
                  <FormLabel>{topupBundleName ? 'Auto top-up threshold' : 'Email reminder threshold'}</FormLabel>
                  <RadioGroup value={threshold} onChange={e => setThreshold(e.target.value)}>
                    <FormControlLabel value="20" control={<Radio />} label="20% remaining" />
                    <FormControlLabel value="10" control={<Radio />} label="10% remaining" />
                    <FormControlLabel value="5" control={<Radio />} label="5% remaining" />
                    <FormControlLabel value="2" control={<Radio />} label="2% remaining" />
                  </RadioGroup>
                </div>
              </>
            ) : null}

            {processing ? (
              <>
                <CircularProgress />
                <Typography>Payment and credit loading is in progress.</Typography>
              </>
            ) : null}

            {!processing && purchaseResult === "success" ? <Typography>Payment was successful. {parseInt(selectedBundle.credits,10).toLocaleString()} credits added.</Typography> : null}
            {!processing && purchaseResult === "failure" ? <Typography>Payment failed. Please check your payment card details.</Typography> : null}
          </div>
        </DialogContent>
        <DialogActions>
          {!processing && purchaseResult === null ? <Button onClick={dismissPurchaseDialog}>Cancel</Button> : null}
          {!processing && purchaseResult === null ? <Button variant='contained' onClick={purchaseBundle}>Confirm purchase</Button> : null}
          {(processing || purchaseResult !== null) ? <Button onClick={dismissPurchaseDialog} disabled={processing}>Dismiss</Button> : null}
        </DialogActions>
      </Dialog>

      <div className='flex flex-row p-5'>
          <div className='flex flex-col gap-5 p-3 items-center'>

            <Typography variant='h4'>Purchase a credit bundle</Typography>

            <FormControlLabel
              control={<><FormLabel>Show prices ex VAT</FormLabel><Switch checked={showIncVat} onChange={e => setShowIncVat(e.target.checked)} /></>}
              label="inc VAT"
            />

            <List component="nav" sx={{width:600}}>
              {priceList.map(bundle => <BundleItem key={bundle.bundle_name} bundle={bundle} selectedBundle={selectedBundle} setSelectedBundle={setSelectedBundle} getDisplayPrice={getDisplayPrice} />)}
            </List>


            <div className='max-w-[600px]'><strong className='capitalize'>{selectedBundle?.bundle_name}</strong> : {selectedBundle?.example}</div>


            {cardValid && selectedBundle && <Button variant='outlined' onClick={openPurchaseDialog} disabled={processing}>Purchase {selectedBundle.bundle_name} credit bundle for £{getDisplayPrice(selectedBundle.price)}</Button>}

            {!cardValid && <Link to="./payment-card" className='link'>Setup payment card before purchase</Link>}
          </div>
      </div>
    </>
  )
}

export default CreditsPriceList

const BundleItem = ({bundle, selectedBundle, setSelectedBundle, getDisplayPrice}) => {

  const handleListItemClick = (event, bdl) => {
    setSelectedBundle(bdl)
  }

  return (
    <ListItemButton
      selected={selectedBundle === bundle}
      onClick={(event) => handleListItemClick(event, bundle)}
    >
      <ListItemIcon>
        {selectedBundle === bundle ? <ShoppingCartOutlinedIcon fontSize="large"/> : null}
      </ListItemIcon>

      <ListItemText primary={<BundleName bundle={bundle}/>} secondary={<BundleDetail bundle={bundle} getDisplayPrice={getDisplayPrice} />}
        slotProps={{
          primary: { component: 'div' },
          secondary: { component: 'div' }
        }}
      />
    </ListItemButton>
  )
}

const BundleName = ({bundle}) => (
  <div className='flex flex-row w-full justify-between'>
    <Typography variant='h5' className='capitalize'>{bundle.bundle_name}</Typography>
    <Typography variant='h6' className='capitalize'>{parseInt(bundle.credits,10).toLocaleString()} credits</Typography>
  </div>
)

const BundleDetail = ({bundle, getDisplayPrice}) => (
  <div className='flex flex-row w-full justify-between text-xs'>
    <div>overdraft limit: <span className='link'>{parseInt(bundle.overdraft,10).toLocaleString()} credits</span></div>
    <div>price : <span className='link'>£{getDisplayPrice(bundle.price)}</span></div>
  </div>
)

const PurchaseItem = ({ title, value }) => (
  <div className='flex flex-row w-full justify-between'>
    <Typography className='font-bold'>{title}</Typography>
    <Typography>{value}</Typography>
  </div>
)
